import { createAction, createAsyncThunk } from '@reduxjs/toolkit'
import type { InputList, InputListInit, InputListSortableField, InputListUpdate, ListResult } from 'common/api/v1/types'
import type { ThunkApi } from '../../store'
import { enqueueErrorSnackbar, enqueueSuccessSnackbar } from './notificationActions'
import { withDefaultPagination } from '../../utils'
import { ApplicationException } from '../../api/ApplicationException'
import type { EnrichedInputList, PaginatedRequestParams } from '../../api/nm-types'

export const createInputList = createAsyncThunk<InputList, InputListInit, ThunkApi>(
  'inputLists/createInputList',
  async (inputList, { dispatch, rejectWithValue, extra: { routes, api, navigate } }) => {
    try {
      const result = await api.inputListsApi.createInputList(inputList)
      navigate()(routes.inputLists())
      dispatch(enqueueSuccessSnackbar(`Created input list ${inputList.name}`))
      return result
    } catch (error) {
      dispatch(enqueueErrorSnackbar({ error, operation: 'create input list' }))
      if (error instanceof ApplicationException) return rejectWithValue(error.errorInfo)
      throw error
    }
  },
)

export const removeInputList = createAsyncThunk<InputList, InputList['id'], ThunkApi>(
  'inputLists/removeInputList',
  async (id, { dispatch, extra: { routes, api, navigate } }) => {
    try {
      const result = await api.inputListsApi.removeInputList(id)
      navigate()(routes.inputLists())
      dispatch(enqueueSuccessSnackbar(`Removed input list: ${result.name}`))
      return result
    } catch (error) {
      dispatch(enqueueErrorSnackbar({ error, operation: 'delete input list' }))
      throw error
    }
  },
)

export const updateInputList = createAsyncThunk<
  InputList,
  { inputList: InputList & InputListUpdate; withRedirection: boolean },
  ThunkApi
>(
  'inputLists/updateInputList',
  async ({ inputList, withRedirection }, { dispatch, rejectWithValue, extra: { routes, api, navigate } }) => {
    try {
      const result = await api.inputListsApi.updateInputList(inputList.id, inputList)
      if (withRedirection) navigate()(routes.inputLists())
      dispatch(enqueueSuccessSnackbar(`Edited input list: ${inputList.name}`))
      return result
    } catch (error) {
      dispatch(enqueueErrorSnackbar({ error, operation: 'update input list' }))
      if (error instanceof ApplicationException) return rejectWithValue(error.errorInfo)
      throw error
    }
  },
)

export const getInputLists = createAsyncThunk<
  ListResult<EnrichedInputList>,
  Partial<PaginatedRequestParams<InputListSortableField>>,
  ThunkApi
>('inputLists/getInputLists', async (params, { dispatch, extra: { api } }) => {
  try {
    return await api.inputListsApi.getInputLists(withDefaultPagination(params))
  } catch (error) {
    dispatch(enqueueErrorSnackbar({ error, operation: 'fetch input lists' }))
    throw error
  }
})

export const getInputList = createAsyncThunk<EnrichedInputList, InputList['id'], ThunkApi>(
  'inputLists/getInputList',
  async (id, { dispatch, extra: { api } }) => {
    try {
      return await api.inputListsApi.getInputList(id)
    } catch (error) {
      dispatch(enqueueErrorSnackbar({ error, operation: 'fetch input list' }))
      throw error
    }
  },
)

export const removeInputLists = createAsyncThunk<string[], InputList['id'][], ThunkApi>(
  'inputLists/removeInputLists',
  async (ids, { dispatch, extra: { api } }) => {
    try {
      for (const id of ids) await api.inputListsApi.removeInputList(id)
      dispatch(enqueueSuccessSnackbar(`${ids.length} input list${ids.length === 1 ? '' : 's'} removed`))
      return ids
    } catch (error) {
      dispatch(enqueueErrorSnackbar({ error, operation: 'delete input lists' }))
      throw error
    }
  },
)

export const clearInputList = createAction('inputLists/clearInputList')
export const clearInputLists = createAction('inputLists/clearInputLists')
